import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Button, Separator } from '@/components/ui'
import { useSEO, createArticleSchema } from '@/hooks/useSEO'
import { useScrollReveal, useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { CoverImage } from '@/components/CoverImage'

export default function SierraCanyonCaseStudyPage() {
  const breadcrumbs = generateBreadcrumbs('/blog/sierra-canyon-case-study', breadcrumbLabels)

  const heroRef = useHeroReveal<HTMLElement>(0.2)
  const sectionsRef = useScrollReveal<HTMLElement>('.article-section', { stagger: 0.1 })

  useSEO({
    title: 'How We Built a Digital Photo Competition for Sierra Canyon School',
    description:
      'Behind the scenes of replacing a paper-based photo competition with a mobile-first digital platform for student submissions, teacher moderation, and anonymous voting.',
    url: '/blog/sierra-canyon-case-study',
    canonical: '/blog/sierra-canyon-case-study',
    type: 'article',
    breadcrumbs,
    article: {
      publishedTime: '2025-03-03',
      author: 'Garrett John Law',
      section: 'Case Studies',
      tags: ['school platforms', 'custom software', 'photo competition', 'education technology'],
    },
    schema: createArticleSchema({
      headline: 'How We Built a Digital Photo Competition for Sierra Canyon School',
      description:
        'Behind the scenes of replacing a paper-based photo competition with a mobile-first digital platform.',
      datePublished: '2025-03-03',
      url: '/blog/sierra-canyon-case-study',
    }),
  })

  return (
    <article ref={sectionsRef} className="mx-auto max-w-3xl space-y-12 px-4 py-16">
      {/* Header */}
      <header ref={heroRef} className="space-y-4">
        <Link to="/blog" className="text-primary text-sm hover:underline">
          &larr; Back to Blog
        </Link>
        <h1 className="text-fluid-h2">How We Built a Digital Photo Competition for Sierra Canyon School</h1>
        <div className="text-muted-foreground flex items-center gap-4 text-sm">
          <time dateTime="2025-03-03">March 3, 2025</time>
          <span>&middot;</span>
          <span>6 min read</span>
        </div>
        <p className="text-fluid-h4 text-muted-foreground">
          Behind the scenes of replacing a paper-based photo competition with a mobile-first digital platform.
        </p>
      </header>

      <CoverImage
        slug="sierra-canyon-case-study"
        alt="Sierra Canyon School's competition platform for student submissions"
      />

      <Separator />

      {/* The Problem */}
      <section className="prose prose-invert article-section max-w-none space-y-4">
        <h2 className="text-fluid-h2">The Problem</h2>
        <p>
          Sierra Canyon School runs an annual photography competition for their students. It&apos;s a big deal for the
          Visual Arts Department. Students submit their best photos, teachers review them, and the school community
          votes on winners.
        </p>
        <p>
          The problem? The whole thing was running on paper. Students would print submission forms, attach their photos,
          and turn them in physically. Teachers would sort through stacks of entries. Voting happened with paper
          ballots.
        </p>
        <p>
          It worked, but it was slow. Danielle, the teacher running the competition, was spending hours on logistics
          that had nothing to do with actually evaluating photography. And students were frustrated too. Half of them
          take photos on iPhones. Printing photos from your phone is annoying.
        </p>
        <p>She reached out asking if there was a better way. There was.</p>
      </section>

      <Separator />

      {/* The Approach */}
      <section className="prose prose-invert article-section max-w-none space-y-4">
        <h2 className="text-fluid-h2">The Approach</h2>
        <p>
          We could have pointed her to an off-the-shelf form builder. Google Forms, Typeform, something like that. But
          none of them would really solve the problem.
        </p>
        <p>Here&apos;s what she actually needed:</p>
        <ul className="list-disc space-y-2 pl-6">
          <li>Students submit photos directly from their phones</li>
          <li>Teachers review and approve submissions before anyone sees them</li>
          <li>Anonymous voting so students don&apos;t just vote for their friends</li>
          <li>Results that update in real-time</li>
        </ul>
        <p>No single tool does all of that. So we built it.</p>
        <p>
          The key decision was making it mobile-first. These are high schoolers. They&apos;re not going to sit down at a
          computer to submit a photo. They&apos;re going to do it from their phone between classes. The whole experience
          had to work perfectly on a 6-inch screen.
        </p>
      </section>

      <Separator />

      {/* The Build */}
      <section className="prose prose-invert article-section max-w-none space-y-4">
        <h2 className="text-fluid-h2">The Build</h2>

        <h3 className="mt-8 text-xl font-semibold">Submission Wizard</h3>
        <p>
          We built a multi-step submission wizard. Students enter their info, upload their photo, add a title and
          description, and submit. Simple. But there was a catch.
        </p>
        <p>
          iPhones save photos in HEIC format. Most web browsers can&apos;t display HEIC images. Teachers reviewing
          submissions on their laptops would just see broken images.
        </p>
        <p>
          So we added automatic conversion. When a student uploads a HEIC photo from their iPhone, we convert it to JPEG
          in the browser before uploading. They don&apos;t have to think about it. It just works.
        </p>

        <h3 className="mt-8 text-xl font-semibold">Teacher Moderation</h3>
        <p>
          Every submission goes to a review queue first. Teachers can approve or reject each entry. If there&apos;s an
          issue with a submission, they can send it back with notes. Only approved photos show up in the voting pool.
        </p>
        <p>
          This was important. You can&apos;t have students voting on unreviewed submissions. And teachers needed a way
          to manage the whole thing without spreadsheets.
        </p>

        <h3 className="mt-8 text-xl font-semibold">Anonymous Voting</h3>
        <p>Here&apos;s the tricky part. How do you let people vote without knowing who submitted what?</p>
        <p>
          We used magic links. When voting opens, eligible voters get an email with a unique link. Click the link,
          you&apos;re in. No login, no password, no account to create. But the link is tied to your email, so you can
          only vote once.
        </p>
        <p>
          We also built two voting modes. Classic voting (pick your favorites) and ELO voting (compare two photos
          head-to-head). The ELO system is borrowed from chess rankings. It&apos;s surprisingly fun.
        </p>

        <h3 className="mt-8 text-xl font-semibold">Results Dashboard</h3>
        <p>
          Real-time results for teachers. They can see which photos are leading, how many votes are in, and who
          hasn&apos;t voted yet. When voting closes, they export the winners.
        </p>
      </section>

      <Separator />

      {/* The Result */}
      <section className="prose prose-invert article-section max-w-none space-y-4">
        <h2 className="text-fluid-h2">The Result</h2>
        <p>
          The platform launched for the 2025-2026 school year. The whole process that used to take Danielle hours now
          takes minutes. No more paper forms. No more printing. No more counting ballots by hand.
        </p>
        <p>
          Students can submit from anywhere on their phones. Teachers can moderate from their laptops. The school
          community can vote with a single tap. And at the end, you get clean results with zero manual counting.
        </p>

        {/* Metrics */}
        <div className="my-8 grid grid-cols-3 gap-4">
          <div className="bg-muted/10 border-border rounded-lg border p-4 text-center">
            <p className="text-primary text-2xl font-bold">2 weeks</p>
            <p className="text-muted-foreground text-sm">Development time</p>
          </div>
          <div className="bg-muted/10 border-border rounded-lg border p-4 text-center">
            <p className="text-primary text-2xl font-bold">100%</p>
            <p className="text-muted-foreground text-sm">Mobile ready</p>
          </div>
          <div className="bg-muted/10 border-border rounded-lg border p-4 text-center">
            <p className="text-primary text-2xl font-bold">0</p>
            <p className="text-muted-foreground text-sm">Paper forms</p>
          </div>
        </div>
      </section>

      <Separator />

      {/* Lessons */}
      <section className="prose prose-invert article-section max-w-none space-y-4">
        <h2 className="text-fluid-h2">What I Learned</h2>
        <p>
          Schools have a lot of manual processes that could be digital. Not everything needs a full platform. But when
          the workflow is unique enough, off-the-shelf tools just create more work.
        </p>
        <p>
          The photo competition is a good example. It&apos;s not a survey. It&apos;s not a contact form. It&apos;s a
          specific workflow with specific needs. Building something custom took two weeks. The alternative was years of
          fighting with spreadsheets.
        </p>
        <p>
          Also: HEIC conversion matters more than you&apos;d think. Half of web development is dealing with edge cases
          that seem obvious in hindsight.
        </p>
      </section>

      <Separator />

      {/* CTA */}
      <section className="bg-primary/5 border-primary/20 article-section space-y-6 rounded-lg border p-8 text-center">
        <h3 className="text-2xl font-semibold">Have a Similar Problem?</h3>
        <p className="text-muted-foreground mx-auto max-w-2xl">
          If your school or organization has a manual process that&apos;s eating up time, we can probably fix that. I
          build custom platforms for education and arts organizations.
        </p>
        <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          >
            <Link to="/contact">
              <Button variant="default" size="lg" className="cursor-pointer">
                Let&apos;s Talk &rarr;
              </Button>
            </Link>
          </motion.div>
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          >
            <Link to="/portfolio/sierra-canyon-photo-competition">
              <Button variant="outline" size="lg" className="cursor-pointer">
                See the Full Project
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <Separator />

      {/* Author Bio */}
      <section className="border-border border-t pt-8">
        <div className="flex items-start gap-4">
          <div className="flex-1">
            <p className="text-lg font-semibold">About the Author</p>
            <p className="text-muted-foreground mt-2">
              <strong>Garrett John Law</strong> is a software engineer and musician who builds digital platforms for
              schools, festivals, and arts organizations. He&apos;s an Interlochen alum and CIM graduate, currently
              serving as organist and choirmaster at Holy Trinity Episcopal Church in Los Angeles.
            </p>
            <div className="mt-4">
              <Link to="/about" className="text-primary text-sm hover:underline">
                Learn more about Fugue Lab &rarr;
              </Link>
            </div>
          </div>
        </div>
      </section>
    </article>
  )
}
